'use client';

import React, { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Box, Button, Drawer, List, Toolbar } from '@mui/material';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icons } from '@peerless/ui-assets/icons';
import { LanguageSelector } from '@peerless/ui-locale';
import { NavigationListener } from '@peerless/ui-navigation';
import { AccountButton } from './AccountButton';

export type MobileNavbarProps = React.PropsWithChildren;

export function MobileNavbar({ children }: MobileNavbarProps) {
  const t = useTranslations('Header');

  const [open, setOpen] = useState<boolean>(false);
  const onClose = useCallback(() => setOpen(false), [setOpen]);

  return (
    <>
      <NavigationListener onNavigate={onClose} />
      <Button
        variant={'outlined'}
        color={'secondary'}
        onClick={() => setOpen(!open)}
        sx={{
          display: { xs: 'flex', lg: 'none' },
        }}
        aria-label={t('close')}
      >
        <FontAwesomeIcon icon={icons.menu} />
      </Button>
      <Drawer
        anchor={'right'}
        component="nav"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        PaperProps={{
          sx: { bgcolor: 'common.white' },
        }}
      >
        <Toolbar
          sx={{
            alignItems: 'center',
            justifyContent: 'flex-end',
            minHeight: '4rem !important',
            padding: '1rem !important',
            gap: 1,
            '& .MuiButton-outlinedSecondary': {
              borderColor: 'gray.light',
            },
          }}
        >
          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
            <LanguageSelector />
          </Box>
          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
            <AccountButton />
          </Box>
          <Button
            variant="outlined"
            size="large"
            color="secondary"
            sx={{
              height: '3rem',
              width: '3rem',
            }}
            onClick={onClose}
          >
            <FontAwesomeIcon icon={icons.close} fixedWidth size="lg" />
          </Button>
        </Toolbar>
        <MenuList>{children}</MenuList>
      </Drawer>
    </>
  );
}

function MenuList({ children }: React.PropsWithChildren) {
  return (
    <List
      sx={{
        p: 0,
        mt: -3,
        pb: 3,
        '.MuiListItem-root': {
          p: 0,
        },

        '.MuiListItemButton-root': {
          '.MuiListItemText-root': {
            textAlign: 'left',
          },

          '&:hover .MuiListItemText-root': {
            color: theme => theme.palette.primary.main,
          },
        },

        '[data-menu-level="1"], & > .MuiListItem-root > .MuiListItemButton-root': {
          // immediate descendant selector used for items in the first level of the menu which are not driven by the navigation tree
          mb: 0,
          mt: 3,
          backgroundColor: 'gray.light',

          '& .MuiTypography-root': {
            fontWeight: 700,
            textTransform: 'uppercase',
            fontSize: '1.25rem',
          },
        },

        '[data-menu-level="2"]': {
          mb: 0,
          mt: 1,
          '& .MuiTypography-root': {
            position: 'relative',
            fontWeight: 700,
            textTransform: 'uppercase',
            '&::after': {
              width: 30,
              height: 2,
              backgroundColor: 'primary.main',
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
            },
          },
        },

        '[data-menu-level="3"]': {
          py: 0,
          mb: 0,

          '& .MuiListItemText-root': {
            color: 'common.black',
          },

          '&:hover .MuiListItemText-root': {
            color: theme => theme.palette.primary.main,
          },
        },
      }}
    >
      {children}
    </List>
  );
}
